import React, { useState } from 'react';
import { Smartphone, Bell, MapPin, WifiOff, TrendingUp, Apple, Play, Layers, CheckCircle, X } from 'lucide-react';

// --- SUCCESS POPUP COMPONENT (Blue Theme for App) ---
const SuccessPopup = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex justify-center items-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center shadow-2xl relative transform transition-all scale-100">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
          <CheckCircle size={40} className="text-blue-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Inquiry Sent!</h3>
        <p className="text-gray-600 mb-6 text-sm leading-relaxed">
          Your app idea is safe with us. Our team has received your details and will contact you shortly.
        </p>
        <button 
          onClick={onClose} 
          className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition w-full shadow-lg"
        >
          Great, Thanks
        </button>
      </div>
    </div>
  );
};

const WithApp: React.FC = () => {
  // --- STATE MANAGEMENT ---
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false); // New Success State
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    phone: '',
    email: '',
    projectBrief: ''
  });

  const openModal = () => setIsQuoteModalOpen(true);
  const closeModal = () => setIsQuoteModalOpen(false);

  // Handle Input Change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // --- FAST SUBMIT LOGIC ---
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
        ...formData,
        serviceType: "App Development"
    };

    // 1. Fire Request in Background (Don't Await)
    // fetch('http://localhost:2025/api/quotation/submit', {
    fetch('https://www.projectinfotech.in/api/quotation/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    }).catch(err => console.error("Background submission error:", err));

    // 2. Show Success Immediately (Fake 500ms delay for UX)
    setTimeout(() => {
        setIsSubmitting(false);
        setFormData({ name: '', companyName: '', phone: '', email: '', projectBrief: '' }); // Reset
        closeModal(); // Close Form
        setShowSuccess(true); // Open Success Popup
    }, 500);
  };

  return (
    <div className="bg-white min-h-screen font-sans relative">
      
      {/* --- HERO SECTION --- */}
      <section className="relative bg-blue-900 text-white overflow-hidden pt-20 pb-32 md:pt-28 md:pb-48">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-blue-800 rounded-full opacity-50 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-blue-600 rounded-full opacity-30 blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10 flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-10 md:mb-0 z-20">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              Put Your Business Directly Into <span className="text-blue-300">Their Pockets</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 text-blue-100 pr-8">
              Websites wait for customers to visit. Apps live on their phones, sending notifications and building loyalty every single day.
            </p>
            <button onClick={openModal} className="bg-blue-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-blue-400 transition shadow-xl inline-flex items-center ring-4 ring-blue-500/30">
               Start Building Your App <Smartphone className="ml-3" />
            </button>
          </div>
          <div className="md:w-1/2 relative z-10 flex justify-center">
              <img 
                src="https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Mobile App Success" 
                className="rounded-3xl shadow-2xl border-8 border-gray-900 rotate-6 hover:rotate-0 transition-all duration-700 max-w-xs md:max-w-sm"
              />
          </div>
        </div>
         <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg className="relative block w-full h-[100px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" className="fill-white"></path>
          </svg>
        </div>
      </section>

      {/* --- WHY MOBILE IS CRITICAL --- */}
       <section className="py-16 px-4 bg-white relative -mt-20 z-30">
        <div className="container mx-auto max-w-5xl">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 flex flex-col md:flex-row gap-8 items-center border border-gray-100">
                <div className="md:w-1/2">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">The Mobile Shift is Real</h2>
                    <p className="text-gray-600 text-lg mb-6">Usually, people spend over 4 hours a day on their phones. 90% of that time is spent in apps, not browsers.</p>
                    <ul className="space-y-4 font-medium text-gray-700">
                        <li className="flex items-center"><TrendingUp className="text-green-500 mr-3" /> 3x Higher Conversion Rates than mobile web.</li>
                        <li className="flex items-center"><Bell className="text-blue-500 mr-3" /> Direct marketing via Push Notifications.</li>
                    </ul>
                </div>
                <div className="md:w-1/2 flex justify-around items-end h-48 w-full bg-gray-50 rounded-xl p-4 border-b-4 border-blue-200">
                    <div className="flex flex-col items-center">
                        <div className="h-20 w-12 bg-gray-300 rounded-t-lg animate-pulse"></div>
                        <span className="text-sm mt-2 font-bold text-gray-500">Desktop</span>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="h-32 w-12 bg-blue-300 rounded-t-lg animate-pulse delay-100"></div>
                        <span className="text-sm mt-2 font-bold text-blue-500">Mobile Web</span>
                    </div>
                     <div className="flex flex-col items-center relative">
                        <div className="absolute -top-8 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-bold">Winner</div>
                        <div className="h-40 w-16 bg-blue-600 rounded-t-lg shadow-lg"></div>
                        <span className="text-sm mt-2 font-bold text-blue-900">Mobile Apps</span>
                    </div>
                </div>
            </div>
        </div>
       </section>

      {/* --- OUR PLATFORMS --- */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">We Cover Every User, On Every Device</h2>
            <p className="text-xl text-gray-600">Choose Native for maximum power or Hybrid for speed and efficiency.</p>
          </div>

          <div className="space-y-24">
            {/* ANDROID SECTION */}
            <div className="flex flex-col md:flex-row items-center gap-12">
                <div className="md:w-1/2 order-2 md:order-1">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-green-100 rounded-lg"><Play className="text-green-600 fill-current" size={32}/></div>
                        <h3 className="text-3xl font-bold text-gray-800">Android Native Development</h3>
                    </div>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                        Reach the largest global audience. We use **Kotlin & Java** to build powerful native apps that utilize full device capabilities like camera, GPS, and sensors flawlessly.
                    </p>
                    <ul className="grid grid-cols-2 gap-4">
                        <li className="flex items-center text-sm font-bold text-gray-700 bg-green-50 p-3 rounded-lg border-l-4 border-green-500"><CheckCircle size={16} className="text-green-600 mr-2"/> Google Play Optimized</li>
                        <li className="flex items-center text-sm font-bold text-gray-700 bg-green-50 p-3 rounded-lg border-l-4 border-green-500"><CheckCircle size={16} className="text-green-600 mr-2"/> High Performance</li>
                    </ul>
                </div>
                <div className="md:w-1/2 order-1 md:order-2 relative group">
                    <div className="absolute inset-0 bg-green-200 rounded-3xl transform rotate-3 group-hover:rotate-6 transition"></div>
                    <img src="https://images.unsplash.com/photo-1605787020600-b9cc5c039898?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Android App Development" className="rounded-3xl shadow-xl relative z-10 border-4 border-white" />
                </div>
            </div>

             {/* iOS SECTION */}
            <div className="flex flex-col md:flex-row items-center gap-12">
                 <div className="md:w-1/2 relative group">
                    <div className="absolute inset-0 bg-gray-200 rounded-3xl transform -rotate-3 group-hover:-rotate-6 transition"></div>
                    <img src="https://images.unsplash.com/photo-1556656793-08538906a9f8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="iOS App Development on iPhones" className="rounded-3xl shadow-xl relative z-10 border-4 border-white" />
                </div>
                <div className="md:w-1/2">
                    <div className="flex items-center gap-3 mb-4">
                         <div className="p-3 bg-gray-100 rounded-lg"><Apple className="text-gray-800 fill-current" size={32}/></div>
                        <h3 className="text-3xl font-bold text-gray-800">iOS Native Development</h3>
                    </div>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                        Target the premium market. Using **Swift**, we create buttery-smooth, highly secure apps for iPhone and iPad that meet Apple's strict App Store standards.
                    </p>
                     <ul className="grid grid-cols-2 gap-4">
                        <li className="flex items-center text-sm font-bold text-gray-700 bg-gray-50 p-3 rounded-lg border-l-4 border-gray-800"><CheckCircle size={16} className="text-gray-800 mr-2"/> App Store Approval</li>
                        <li className="flex items-center text-sm font-bold text-gray-700 bg-gray-50 p-3 rounded-lg border-l-4 border-gray-800"><CheckCircle size={16} className="text-gray-800 mr-2"/> Premium UX/UI</li>
                    </ul>
                </div>
            </div>

             {/* FLUTTER SECTION */}
            <div className="flex flex-col md:flex-row items-center gap-12">
                <div className="md:w-1/2 order-2 md:order-1">
                    <div className="flex items-center gap-3 mb-4">
                         <div className="p-3 bg-blue-100 rounded-lg"><Layers className="text-blue-500" size={32}/></div>
                        <h3 className="text-3xl font-bold text-gray-800">Flutter Hybrid Development</h3>
                    </div>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                        **Two apps, one code.** Google's Flutter allows us to build beautiful, natively compiled applications for mobile, web, and desktop from a single codebase.
                    </p>
                     <div className="bg-blue-50 p-6 rounded-xl border-2 border-dashed border-blue-200 mb-6">
                        <h4 className="font-bold text-blue-800 mb-2 flex items-center"><TrendingUp size={18} className="mr-2"/> Business Benefit:</h4>
                        <p className="text-blue-900 font-medium">Save up to 40% on development time and cost compared to building separate native apps.</p>
                     </div>
                </div>
                 <div className="md:w-1/2 order-1 md:order-2 relative group">
                    <div className="absolute inset-0 bg-blue-200 rounded-3xl transform rotate-3 group-hover:rotate-6 transition"></div>
                    <img src="https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Flutter Cross Platform" className="rounded-3xl shadow-xl relative z-10 border-4 border-white" />
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- APP FEATURES --- */}
      <section className="py-16 px-4 bg-gray-900 text-white">
        <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold mb-12">Features That Turn Users Into Loyal Customers</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="p-6 bg-gray-800 rounded-2xl hover:bg-gray-700 transition">
                    <Bell size={40} className="text-blue-400 mx-auto mb-4"/>
                    <h4 className="font-bold text-lg mb-2">Push Notifications</h4>
                    <p className="text-gray-400 text-sm">Keep users engaged with instant alerts.</p>
                </div>
                 <div className="p-6 bg-gray-800 rounded-2xl hover:bg-gray-700 transition">
                    <WifiOff size={40} className="text-green-400 mx-auto mb-4"/>
                    <h4 className="font-bold text-lg mb-2">Offline Access</h4>
                    <p className="text-gray-400 text-sm">Access core content without internet.</p>
                </div>
                 <div className="p-6 bg-gray-800 rounded-2xl hover:bg-gray-700 transition">
                    <MapPin size={40} className="text-red-400 mx-auto mb-4"/>
                    <h4 className="font-bold text-lg mb-2">GPS & Location</h4>
                    <p className="text-gray-400 text-sm">Offer location-based services.</p>
                </div>
                 <div className="p-6 bg-gray-800 rounded-2xl hover:bg-gray-700 transition">
                    <Smartphone size={40} className="text-purple-400 mx-auto mb-4"/>
                    <h4 className="font-bold text-lg mb-2">Device Integration</h4>
                    <p className="text-gray-400 text-sm">Use camera & contacts seamlessly.</p>
                </div>
            </div>
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <section id="contact" className="py-20 px-4 bg-blue-600 text-white text-center">
        <div className="container mx-auto max-w-3xl">
            <h2 className="text-4xl font-bold mb-6">Have an App Idea?</h2>
            <p className="text-xl text-blue-100 mb-10">Let's discuss how we can bring it to life on the screens of millions of users.</p>
            
            <div className="flex flex-col md:flex-row justify-center gap-4">
                <a href="tel:+917782918158" className="bg-white text-blue-900 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg flex items-center justify-center">
                    Call Us Now
                </a>
                <button onClick={openModal} className="border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition">
                    Request A Quote
                </button>
            </div>
        </div>
      </section>

       {/* --- QUOTATION MODAL FORM --- */}
       {isQuoteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-lg relative shadow-2xl animate-slide-up">
                <button onClick={closeModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 bg-gray-100 rounded-full p-1 transition">
                    <X size={24} />
                </button>
                
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Kickstart Your App Idea</h3>
                <p className="text-gray-600 mb-6 text-sm">Share your vision, get a free estimate.</p>
                
                <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                        <input 
                            type="text" 
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required 
                            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Jane Doe"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Name (Optional)</label>
                        <input 
                            type="text" 
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Your Startup Inc."
                        />
                    </div>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                            <input 
                                type="tel" 
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required 
                                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none" 
                                placeholder="+91..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                            <input 
                                type="email" 
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required 
                                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none" 
                                placeholder="jane@example.com"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">App Idea Brief</label>
                        <textarea 
                            name="projectBrief"
                            value={formData.projectBrief}
                            onChange={handleInputChange}
                            required 
                            rows={4} 
                            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none resize-none" 
                            placeholder="I want to build an app like..."
                        ></textarea>
                    </div>
                    <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white font-bold text-lg py-3 rounded-lg hover:bg-blue-700 transition shadow-lg mt-4 disabled:bg-gray-400">
                        {isSubmitting ? 'Sending...' : 'Submit App Request'}
                    </button>
                </form>
            </div>
        </div>
      )}
      
      {/* --- RENDER NEW SUCCESS POPUP --- */}
      <SuccessPopup isOpen={showSuccess} onClose={() => setShowSuccess(false)} />
    </div>
  );
};

export default WithApp;